import type React from 'react'
import { useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import useIsMobile from '@/hooks/useIsMobile'
import { theme } from '@/lib/theme'
import Button from './Button'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'info'
}

/**
 * Modal component for displaying confirmation dialogs and important messages.
 * Supports different variants for different types of actions.
 */
export default function Modal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'info',
}: ModalProps) {
  // NOTE: useIsMobile detects touch devices for touch-optimized modal layout
  const isMobile = useIsMobile()
  // Close on Escape key
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, handleEscape])

  if (!isOpen) return null

  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          borderColor: theme.colors.errorBorder,
        }
      case 'warning':
        return {
          borderColor: theme.colors.primaryAccent,
        }
      default:
        return {
          borderColor: theme.colors.primaryBorder,
        }
    }
  }

  const overlayStyles: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999999,
    padding: '24px',
  }

  const modalStyles: React.CSSProperties = {
    backgroundColor: theme.colors.primaryBg,
    border: `1px solid ${getVariantStyles().borderColor}`,
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '320px',
    width: '100%',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    fontFamily: '"Departure Mono", monospace',
  }

  const titleStyles: React.CSSProperties = {
    fontSize: isMobile ? '14px' : theme.fontSize.lg,
    fontWeight: 'bold',
    color: theme.colors.primaryText,
    marginBottom: isMobile ? '10px' : theme.spacing.md,
    fontFamily: '"Departure Mono", monospace',
  }

  const messageStyles: React.CSSProperties = {
    fontSize: isMobile ? '12px' : theme.fontSize.sm,
    color: theme.colors.secondaryText,
    marginBottom: isMobile ? '16px' : theme.spacing.lg,
    fontFamily: '"Departure Mono", monospace',
    lineHeight: 1.5,
  }

  const buttonContainerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: '12px',
  }

  return createPortal(
    <div style={overlayStyles} onClick={onClose}>
      <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
        <div style={titleStyles}>{title}</div>
        <div style={messageStyles}>{message}</div>
        <div style={buttonContainerStyles}>
          <Button variant="secondary" onClick={onClose}>
            {cancelText}
          </Button>
          <Button variant="primary" onClick={onConfirm}>
            {confirmText}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}
